/**
 * Compras — configuração D1 (Round D). Embutida em Catálogos → Compras.
 *
 * 3 seções: catálogo de **tipos de item**, cadastro de **fornecedores** (+bloco
 * faturamento direto + tipos que fornece) e **alíquotas de NF por empresa**.
 * Gated por COMPRAS_ENABLED (a aba já gateia; gate redundante por defesa).
 * Dinheiro em CENTAVOS. ZERO localStorage.
 */
import { useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  HelpTip,
  Input,
  Label,
  PageHeader,
  Select,
  SelectOption,
  Switch,
  useToast,
  useConfirmDialog,
} from '@/components/ui';
import { errMsg } from '@/lib/api-error';
import { parseNumeroBr } from '@/lib/format';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import {
  useTiposItem,
  useSetores,
  useFormasPagamento,
  useOrigens,
  useEmpresasCompras,
  useComprasActions,
  useComprasNotifConfig,
  useSetComprasNotifConfig,
  type CompraTipoItem,
  type CompraSetor,
  type CompraFormaPagamento,
  type CompraOrigem,
  type OrigemModo,
  type EmpresaCompras,
} from '@/lib/compras-store';

export function ComprasConfigPage({ embedded = false }: { embedded?: boolean }) {
  const comprasOn = useMyFeatureFlag('COMPRAS_ENABLED');
  if (!comprasOn) {
    if (embedded) return null;
    return (
      <div className="min-w-0">
        <EmptyState
          title="Módulo de Compras indisponível"
          description="O módulo de Compras ainda não está ativado."
        />
      </div>
    );
  }
  // Embutida na aba "Compras" de Catálogos: a página hospedeira já tem o h1 —
  // aqui entra só um título de seção, sem padding próprio.
  return (
    <div className="min-w-0">
      {embedded ? (
        <h2 className="mb-1 font-display text-xl tracking-tight text-ink-1 dark:text-ink-dark-1">
          Configurações de Compras
        </h2>
      ) : (
        <PageHeader
          crumb="Configurações · Cadastros"
          title="Configurações de Compras"
          subtitle="Tipos de item, setores, formas de pagamento, fornecedores e as regras de nota fiscal por empresa."
        />
      )}
      <p className="mb-5 text-sm text-ink-2 dark:text-ink-dark-2">
        Tudo que o módulo de Compras usa para montar uma cotação: as listas escolhidas em cada item,
        o cadastro de fornecedores (com faturamento direto) e a alíquota de nota fiscal de cada
        empresa.
      </p>
      <div className="flex flex-col gap-6">
        <TiposItemSection />
        <SetoresSection />
        <FormasPagamentoSection />
        <OrigensSection />
        {/* 🔴 Card de Fornecedores REMOVIDO (mig 467, passo c). O fornecedor
            agora vive na ficha do cadastro, em Clientes — quem tem a etiqueta
            `Fornecedor` tem a aba. Tinha DOIS caminhos para o mesmo dado; agora
            tem um. As outras sete seções desta tela continuam. */}
        <AliquotasSection />
        {/* Crédito por fornecedor também foi para a ficha, junto — decisão do
            Matheus: tudo do fornecedor num lugar só. Tinha zero lançamentos, o
            que tornou a mudança barata; depois do primeiro, não seria. */}
        <NotificacoesSection />
      </div>
    </div>
  );
}

// ─── 1. Tipos de item ───────────────────────────────────────────────────
function TiposItemSection() {
  const { data, isLoading, isError } = useTiposItem();
  const { createTipo, updateTipo, deleteTipo } = useComprasActions();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [novo, setNovo] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editNome, setEditNome] = useState('');

  const tipos = data?.items ?? [];

  function adicionar() {
    const nome = novo.trim();
    if (!nome) return;
    createTipo.mutate(
      { nome },
      {
        onSuccess: () => setNovo(''),
        onError: (e) =>
          toast.show(errMsg(e) ?? 'Não foi possível adicionar o tipo.', { variant: 'error' }),
      }
    );
  }
  function commitEdit(id: string) {
    const nome = editNome.trim();
    if (nome) {
      updateTipo.mutate(
        { id, patch: { nome } },
        {
          onError: (e) =>
            toast.show(errMsg(e) ?? 'Não foi possível renomear.', { variant: 'error' }),
        }
      );
    }
    setEditId(null);
  }
  async function excluir(t: CompraTipoItem) {
    const ok = await confirm({
      title: 'Excluir tipo de item?',
      message: `Excluir "${t.nome}"? Os fornecedores deixam de listar esse tipo.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    deleteTipo.mutate(t.id, {
      onSuccess: () => toast.show('Tipo excluído'),
      onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível excluir.', { variant: 'error' }),
    });
  }

  return (
    <Card variant="flat" className="p-4">
      <h3 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1">Tipos de item</h3>
      <p className="mb-3 mt-0.5 text-sm text-ink-2 dark:text-ink-dark-2">
        Categorias de item (ex.: câmera, cabo, fonte). A cotação usa isso pra sugerir fornecedores
        por item.
      </p>

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : isError ? (
        <Alert variant="danger">Não foi possível carregar os tipos de item.</Alert>
      ) : (
        <ul className="mb-3 flex flex-col gap-1">
          {tipos.map((t) => (
            <li
              key={t.id}
              data-testid={`compra-tipo-${t.id}`}
              className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 rounded-lg border border-line bg-paper-surface px-3 py-1.5 dark:border-line-dark dark:bg-paper-dark-surface"
            >
              {editId === t.id ? (
                <Input
                  size="sm"
                  autoFocus
                  value={editNome}
                  onChange={(e) => setEditNome(e.target.value)}
                  onBlur={() => commitEdit(t.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitEdit(t.id);
                    else if (e.key === 'Escape') setEditId(null);
                  }}
                  wrapperClassName="flex-1 min-w-0"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditId(t.id);
                    setEditNome(t.nome);
                  }}
                  className="min-w-0 flex-1 truncate text-left text-sm text-ink-1 hover:text-red dark:text-ink-dark-1"
                >
                  {t.nome}
                  {!t.ativo && (
                    <Badge variant="neutral" size="sm" className="ml-2">
                      Inativo
                    </Badge>
                  )}
                </button>
              )}
              <span className="flex shrink-0 items-center gap-2">
                <Switch
                  checked={t.ativo}
                  onChange={() =>
                    updateTipo.mutate(
                      { id: t.id, patch: { ativo: !t.ativo } },
                      {
                        onError: (e) =>
                          toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', {
                            variant: 'error',
                          }),
                      }
                    )
                  }
                  aria-label={`Ativar ${t.nome}`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
                  onClick={() => excluir(t)}
                >
                  Excluir
                </Button>
              </span>
            </li>
          ))}
          {tipos.length === 0 && (
            <li className="px-3 py-1.5 text-sm text-ink-3 dark:text-ink-dark-3">
              Nenhum tipo ainda.
            </li>
          )}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="max-w-xs flex-1">
          <Label htmlFor="novo-tipo">Novo tipo</Label>
          <Input
            id="novo-tipo"
            size="sm"
            value={novo}
            placeholder="Ex.: Câmera"
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') adicionar();
            }}
          />
        </div>
        <Button size="sm" onClick={adicionar} disabled={!novo.trim() || createTipo.isPending}>
          Adicionar
        </Button>
      </div>
    </Card>
  );
}

// ─── 1b. Setores (FX-8 — uso interno) ───────────────────────────────────
function SetoresSection() {
  const { data, isLoading, isError } = useSetores();
  const { createSetor, updateSetor, deleteSetor } = useComprasActions();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [novo, setNovo] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editNome, setEditNome] = useState('');

  const setores = data?.items ?? [];

  function adicionar() {
    const nome = novo.trim();
    if (!nome) return;
    createSetor.mutate(
      { nome },
      {
        onSuccess: () => setNovo(''),
        onError: (e) =>
          toast.show(errMsg(e) ?? 'Não foi possível adicionar o setor.', { variant: 'error' }),
      }
    );
  }
  function commitEdit(id: string) {
    const nome = editNome.trim();
    if (nome) {
      updateSetor.mutate(
        { id, patch: { nome } },
        {
          onError: (e) =>
            toast.show(errMsg(e) ?? 'Não foi possível renomear.', { variant: 'error' }),
        }
      );
    }
    setEditId(null);
  }
  async function excluir(s: CompraSetor) {
    const ok = await confirm({
      title: 'Excluir setor?',
      message: `Excluir "${s.nome}"? As solicitações que usavam esse setor ficam sem setor.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    deleteSetor.mutate(s.id, {
      onSuccess: () => toast.show('Setor excluído'),
      onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível excluir.', { variant: 'error' }),
    });
  }

  return (
    <Card variant="flat" className="p-4">
      <h3 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1">
        Setores (uso interno)
      </h3>
      <p className="mb-3 mt-0.5 text-sm text-ink-2 dark:text-ink-dark-2">
        Setores da empresa (ex.: TI, Administrativo, Manutenção interna). Usados no vínculo
        <strong> Uso interno </strong> da solicitação de compra.
      </p>

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : isError ? (
        <Alert variant="danger">Não foi possível carregar os setores.</Alert>
      ) : (
        <ul className="mb-3 flex flex-col gap-1">
          {setores.map((s) => (
            <li
              key={s.id}
              data-testid={`compra-setor-${s.id}`}
              className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 rounded-lg border border-line bg-paper-surface px-3 py-1.5 dark:border-line-dark dark:bg-paper-dark-surface"
            >
              {editId === s.id ? (
                <Input
                  size="sm"
                  autoFocus
                  value={editNome}
                  onChange={(e) => setEditNome(e.target.value)}
                  onBlur={() => commitEdit(s.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitEdit(s.id);
                    else if (e.key === 'Escape') setEditId(null);
                  }}
                  wrapperClassName="flex-1 min-w-0"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditId(s.id);
                    setEditNome(s.nome);
                  }}
                  className="min-w-0 flex-1 truncate text-left text-sm text-ink-1 hover:text-red dark:text-ink-dark-1"
                >
                  {s.nome}
                  {!s.ativo && (
                    <Badge variant="neutral" size="sm" className="ml-2">
                      Inativo
                    </Badge>
                  )}
                </button>
              )}
              <span className="flex shrink-0 items-center gap-2">
                <Switch
                  checked={s.ativo}
                  onChange={() =>
                    updateSetor.mutate(
                      { id: s.id, patch: { ativo: !s.ativo } },
                      {
                        onError: (e) =>
                          toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', {
                            variant: 'error',
                          }),
                      }
                    )
                  }
                  aria-label={`Ativar ${s.nome}`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
                  onClick={() => excluir(s)}
                >
                  Excluir
                </Button>
              </span>
            </li>
          ))}
          {setores.length === 0 && (
            <li className="px-3 py-1.5 text-sm text-ink-3 dark:text-ink-dark-3">
              Nenhum setor ainda.
            </li>
          )}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="max-w-xs flex-1">
          <Label htmlFor="novo-setor">Novo setor</Label>
          <Input
            id="novo-setor"
            size="sm"
            value={novo}
            placeholder="Ex.: TI"
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') adicionar();
            }}
          />
        </div>
        <Button size="sm" onClick={adicionar} disabled={!novo.trim() || createSetor.isPending}>
          Adicionar
        </Button>
      </div>
    </Card>
  );
}

// ─── 1c. Formas de pagamento (FX-24) ────────────────────────────────────
function FormasPagamentoSection() {
  const { data, isLoading, isError } = useFormasPagamento();
  const { createFormaPagamento, updateFormaPagamento, deleteFormaPagamento } = useComprasActions();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [novo, setNovo] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editNome, setEditNome] = useState('');

  const formas = data?.items ?? [];

  function adicionar() {
    const nome = novo.trim();
    if (!nome) return;
    createFormaPagamento.mutate(
      { nome },
      {
        onSuccess: () => setNovo(''),
        onError: (e) =>
          toast.show(errMsg(e) ?? 'Não foi possível adicionar a forma de pagamento.', {
            variant: 'error',
          }),
      }
    );
  }
  function commitEdit(id: string) {
    const nome = editNome.trim();
    if (nome) {
      updateFormaPagamento.mutate(
        { id, patch: { nome } },
        {
          onError: (e) =>
            toast.show(errMsg(e) ?? 'Não foi possível renomear.', { variant: 'error' }),
        }
      );
    }
    setEditId(null);
  }
  async function excluir(f: CompraFormaPagamento) {
    const ok = await confirm({
      title: 'Excluir forma de pagamento?',
      message: `Excluir "${f.nome}"? As cotações que usavam essa forma ficam sem forma de pagamento.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    deleteFormaPagamento.mutate(f.id, {
      onSuccess: () => toast.show('Forma de pagamento excluída'),
      onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível excluir.', { variant: 'error' }),
    });
  }

  return (
    <Card variant="flat" className="p-4">
      <h3 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1">
        Formas de pagamento
      </h3>
      <p className="mb-3 mt-0.5 text-sm text-ink-2 dark:text-ink-dark-2">
        Formas de pagamento (ex.: Boleto, PIX, Cartão). Escolhidas no item da cotação e enviadas ao
        Financeiro junto da aprovação.
      </p>

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : isError ? (
        <Alert variant="danger">Não foi possível carregar as formas de pagamento.</Alert>
      ) : (
        <ul className="mb-3 flex flex-col gap-1">
          {formas.map((f) => (
            <li
              key={f.id}
              data-testid={`compra-forma-pagamento-${f.id}`}
              className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 rounded-lg border border-line bg-paper-surface px-3 py-1.5 dark:border-line-dark dark:bg-paper-dark-surface"
            >
              {editId === f.id ? (
                <Input
                  size="sm"
                  autoFocus
                  value={editNome}
                  onChange={(e) => setEditNome(e.target.value)}
                  onBlur={() => commitEdit(f.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitEdit(f.id);
                    else if (e.key === 'Escape') setEditId(null);
                  }}
                  wrapperClassName="flex-1 min-w-0"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditId(f.id);
                    setEditNome(f.nome);
                  }}
                  className="min-w-0 flex-1 truncate text-left text-sm text-ink-1 hover:text-red dark:text-ink-dark-1"
                >
                  {f.nome}
                  {!f.ativo && (
                    <Badge variant="neutral" size="sm" className="ml-2">
                      Inativo
                    </Badge>
                  )}
                </button>
              )}
              <span className="flex shrink-0 items-center gap-2">
                <Switch
                  checked={f.ativo}
                  onChange={() =>
                    updateFormaPagamento.mutate(
                      { id: f.id, patch: { ativo: !f.ativo } },
                      {
                        onError: (e) =>
                          toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', {
                            variant: 'error',
                          }),
                      }
                    )
                  }
                  aria-label={`Ativar ${f.nome}`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
                  onClick={() => excluir(f)}
                >
                  Excluir
                </Button>
              </span>
            </li>
          ))}
          {formas.length === 0 && (
            <li className="px-3 py-1.5 text-sm text-ink-3 dark:text-ink-dark-3">
              Nenhuma forma de pagamento ainda.
            </li>
          )}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="max-w-xs flex-1">
          <Label htmlFor="nova-forma-pagamento">Nova forma de pagamento</Label>
          <Input
            id="nova-forma-pagamento"
            size="sm"
            value={novo}
            placeholder="Ex.: Boleto"
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') adicionar();
            }}
          />
        </div>
        <Button
          size="sm"
          onClick={adicionar}
          disabled={!novo.trim() || createFormaPagamento.isPending}
        >
          Adicionar
        </Button>
      </div>
    </Card>
  );
}

// ─── 2. Fornecedores ────────────────────────────────────────────────────
// ── FornecedoresSection REMOVIDO (mig 467, passo c) ──────────────────────────────
// ─── 3. Empresas: apelido + alíquota de NF ──────────────────────────────
function AliquotasSection() {
  const { data, isLoading, isError } = useEmpresasCompras();
  const { setAliquota, setAliquotaServico, setApelido } = useComprasActions();
  const toast = useToast();
  const empresas = data?.items ?? [];

  return (
    // A ancora existe para o atalho "Impostos" do Financeiro poder apontar
    // AQUI, e nao para o topo do painel. Sem ela, os dois itens teriam o mesmo
    // destino — e dois itens pro mesmo lugar e adivinhacao (ha guarda).
    <Card variant="flat" className="p-4" id="aliquota-nf">
      <h3 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1">
        Empresas (apelido e alíquota de nota fiscal)
      </h3>
      <p className="mb-3 mt-0.5 text-sm text-ink-2 dark:text-ink-dark-2">
        O apelido é o nome curto que aparece nas listas de escolha (ex.: na empresa do faturamento
        direto) — útil porque as duas empresas IGM têm o mesmo nome de fantasia. A alíquota entra no
        cálculo do faturamento direto quando o fornecedor pede nota fiscal.
      </p>
      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : isError ? (
        <Alert variant="danger">Não foi possível carregar as empresas.</Alert>
      ) : (
        <ul className="flex flex-col gap-2">
          {empresas.map((e) => (
            <EmpresaRow
              key={e.id}
              empresa={e}
              onSaveAliquota={(pct) =>
                setAliquota.mutate(
                  { id: e.id, aliquotaNfPct: pct },
                  {
                    // CO-4: confirma o save silencioso da alíquota.
                    onSuccess: () => toast.show('Alíquota salva', { variant: 'success' }),
                    onError: (err) =>
                      toast.show(errMsg(err) ?? 'Não foi possível salvar a alíquota.', {
                        variant: 'error',
                      }),
                  }
                )
              }
              onSaveAliquotaServico={(pct) =>
                setAliquotaServico.mutate(
                  { id: e.id, aliquotaNfServicoPct: pct },
                  {
                    onSuccess: () =>
                      toast.show('Alíquota de serviço salva', { variant: 'success' }),
                    onError: (err) =>
                      toast.show(errMsg(err) ?? 'Não foi possível salvar a alíquota.', {
                        variant: 'error',
                      }),
                  }
                )
              }
              onSaveApelido={(apelido) =>
                setApelido.mutate(
                  { id: e.id, apelido },
                  {
                    onSuccess: () => toast.show('Apelido salvo', { variant: 'success' }),
                    onError: (err) =>
                      toast.show(errMsg(err) ?? 'Não foi possível salvar o apelido.', {
                        variant: 'error',
                      }),
                  }
                )
              }
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function EmpresaRow({
  empresa,
  onSaveAliquota,
  onSaveAliquotaServico,
  onSaveApelido,
}: {
  empresa: EmpresaCompras;
  onSaveAliquota: (pct: number) => void;
  onSaveAliquotaServico: (pct: number | null) => void;
  onSaveApelido: (apelido: string | null) => void;
}) {
  const { id, razaoSocial, cnpj, aliquotaNfPct, aliquotaNfServicoPct, apelido } = empresa;
  const [aliqVal, setAliqVal] = useState(String(aliquotaNfPct));
  const [aliqServVal, setAliqServVal] = useState(
    aliquotaNfServicoPct === null ? '' : String(aliquotaNfServicoPct)
  );
  const [apelidoVal, setApelidoVal] = useState(apelido ?? '');
  useEffect(() => setAliqVal(String(aliquotaNfPct)), [aliquotaNfPct]);
  useEffect(
    () => setAliqServVal(aliquotaNfServicoPct === null ? '' : String(aliquotaNfServicoPct)),
    [aliquotaNfServicoPct]
  );
  useEffect(() => setApelidoVal(apelido ?? ''), [apelido]);

  function commitAliquota() {
    const n = parseNumeroBr(aliqVal);
    if (n !== null && n >= 0 && n !== aliquotaNfPct) onSaveAliquota(n);
  }
  function commitAliquotaServico() {
    // Vazio = limpa (volta a usar a de produto) — nunca grava 0 sem querer.
    if (aliqServVal.trim() === '') {
      if (aliquotaNfServicoPct !== null) onSaveAliquotaServico(null);
      return;
    }
    const n = parseNumeroBr(aliqServVal);
    if (n !== null && n >= 0 && n !== aliquotaNfServicoPct) onSaveAliquotaServico(n);
  }
  function commitApelido() {
    const novo = apelidoVal.trim() || null;
    if (novo !== (apelido ?? null)) onSaveApelido(novo);
  }

  return (
    <li
      data-testid={`compra-empresa-${id}`}
      className="flex flex-col gap-2 rounded-lg border border-line bg-paper-surface px-3 py-2 dark:border-line-dark dark:bg-paper-dark-surface"
    >
      {/* FX-5: razão social + CNPJ sempre visíveis (diferencia as 2 IGM). */}
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          {razaoSocial}
        </p>
        <p className="text-xs tabular-nums text-ink-3 dark:text-ink-dark-3">{cnpj}</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-0 flex-col gap-0.5">
          <span className="text-xs text-ink-2 dark:text-ink-dark-2">Apelido (nome curto)</span>
          <Input
            size="sm"
            data-testid={`compra-apelido-${id}`}
            value={apelidoVal}
            placeholder="ex.: IGM AG"
            maxLength={60}
            onChange={(e) => setApelidoVal(e.target.value)}
            onBlur={commitApelido}
            wrapperClassName="w-44 max-w-full"
          />
        </label>
        <label className="flex min-w-0 flex-col gap-0.5">
          <span className="inline-flex items-center gap-1 text-xs text-ink-2 dark:text-ink-dark-2">
            Alíquota de NF — produto
            <HelpTip texto="Incide na conta do orçamento sobre o GANHO no material (venda − custo − imposto do fornecedor, no faturamento direto). Também é a alíquota do faturamento direto no Compras. Orçamento já criado guarda a alíquota do dia em que nasceu — trocar aqui só muda os novos." />
          </span>
          <span className="flex items-center gap-1">
            <Input
              type="number"
              size="sm"
              data-testid={`compra-aliquota-${id}`}
              value={aliqVal}
              onChange={(e) => setAliqVal(e.target.value)}
              onBlur={commitAliquota}
              className="w-24 text-right"
            />
            <span className="text-sm text-ink-3 dark:text-ink-dark-3">%</span>
          </span>
        </label>
        <label className="flex min-w-0 flex-col gap-0.5">
          <span className="inline-flex items-center gap-1 text-xs text-ink-2 dark:text-ink-dark-2">
            Alíquota de NF — serviço
            <HelpTip texto="Incide sobre a VENDA CHEIA da mão de obra no orçamento (serviço não tem custo de material a deduzir). Vazio = usa a alíquota de produto. Orçamento já criado não muda — a alíquota nova vale para os novos." />
          </span>
          <span className="flex items-center gap-1">
            <Input
              type="number"
              size="sm"
              data-testid={`compra-aliquota-servico-${id}`}
              value={aliqServVal}
              placeholder="= produto"
              onChange={(e) => setAliqServVal(e.target.value)}
              onBlur={commitAliquotaServico}
              className="w-24 text-right"
            />
            <span className="text-sm text-ink-3 dark:text-ink-dark-3">%</span>
          </span>
        </label>
      </div>
    </li>
  );
}

// ─── 1d. Origens do item da cotação (FX-16) ─────────────────────────────
const ORIGEM_SEED_IDS = ['comercial', 'manutencao'];
function modoLabel(o: CompraOrigem): string {
  return o.modo === 'custo_venda' ? `custo × ${o.fator}` : `venda ÷ ${o.fator}`;
}
function OrigensSection() {
  const { data, isLoading, isError } = useOrigens();
  const { createOrigem, updateOrigem, deleteOrigem } = useComprasActions();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [novoNome, setNovoNome] = useState('');
  const [novoModo, setNovoModo] = useState<OrigemModo>('custo_venda');
  const [novoFator, setNovoFator] = useState('1,4');

  const origens = data?.items ?? [];

  function adicionar() {
    const nome = novoNome.trim();
    const fator = parseNumeroBr(novoFator);
    if (!nome) return;
    if (fator === null || fator <= 0) {
      toast.show('Informe um fator maior que zero.', { variant: 'error' });
      return;
    }
    createOrigem.mutate(
      { nome, modo: novoModo, fator },
      {
        onSuccess: () => {
          setNovoNome('');
          setNovoFator('1,4');
          setNovoModo('custo_venda');
        },
        onError: (e) =>
          toast.show(errMsg(e) ?? 'Não foi possível adicionar a origem.', { variant: 'error' }),
      }
    );
  }
  function patch(
    id: string,
    p: { nome?: string; modo?: OrigemModo; fator?: number; ativo?: boolean }
  ) {
    updateOrigem.mutate(
      { id, patch: p },
      { onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível salvar.', { variant: 'error' }) }
    );
  }
  async function excluir(o: CompraOrigem) {
    const ok = await confirm({
      title: 'Excluir origem?',
      message: `Excluir "${o.nome}"? Os itens que usavam essa origem precisam ser reclassificados.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    deleteOrigem.mutate(o.id, {
      onSuccess: () => toast.show('Origem excluída'),
      onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível excluir.', { variant: 'error' }),
    });
  }

  return (
    <Card variant="flat" className="p-4">
      <h3 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1">Origens do item</h3>
      <p className="mb-3 mt-0.5 text-sm text-ink-2 dark:text-ink-dark-2">
        Origem de cada item da cotação + a regra de markup. <strong>custo × fator</strong> (ex.:
        Comercial ×1,4) usa o mínimo de venda no faturamento direto; <strong>venda ÷ fator</strong>{' '}
        (ex.: Manutenção ÷1,5) usa o mínimo de manutenção. Comercial e Manutenção não podem ser
        excluídas.
      </p>

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : isError ? (
        <Alert variant="danger">Não foi possível carregar as origens.</Alert>
      ) : (
        <ul className="mb-3 flex flex-col gap-1">
          {origens.map((o) => (
            <li
              key={o.id}
              data-testid={`compra-origem-${o.id}`}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-paper-surface px-3 py-1.5 dark:border-line-dark dark:bg-paper-dark-surface"
            >
              <span className="min-w-[100px] flex-1 truncate text-sm text-ink-1 dark:text-ink-dark-1">
                {o.nome}
                {!o.ativo && (
                  <Badge variant="neutral" size="sm" className="ml-2">
                    Inativo
                  </Badge>
                )}
              </span>
              <Select
                size="sm"
                value={o.modo}
                onChange={(e) => patch(o.id, { modo: e.target.value as OrigemModo })}
                wrapperClassName="w-32 shrink-0"
                aria-label={`Modo de ${o.nome}`}
              >
                <SelectOption value="custo_venda">custo × fator</SelectOption>
                <SelectOption value="venda_custo">venda ÷ fator</SelectOption>
              </Select>
              <Input
                size="sm"
                defaultValue={String(o.fator).replace('.', ',')}
                wrapperClassName="w-16 shrink-0"
                className="text-right"
                aria-label={`Fator de ${o.nome}`}
                onBlur={(e) => {
                  const f = parseNumeroBr(e.target.value);
                  if (f !== null && f > 0 && f !== o.fator) patch(o.id, { fator: f });
                }}
              />
              <span className="w-24 shrink-0 text-right text-xs tabular-nums text-ink-3 dark:text-ink-dark-3">
                {modoLabel(o)}
              </span>
              <Switch
                checked={o.ativo}
                onChange={() => patch(o.id, { ativo: !o.ativo })}
                aria-label={`Ativar ${o.nome}`}
              />
              <Button
                size="sm"
                variant="ghost"
                className="text-red hover:bg-red-soft disabled:opacity-40 dark:hover:bg-red-soft-dark"
                disabled={ORIGEM_SEED_IDS.includes(o.id)}
                onClick={() => excluir(o)}
              >
                Excluir
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[140px] max-w-xs flex-1">
          <Label htmlFor="nova-origem">Nova origem</Label>
          <Input
            id="nova-origem"
            size="sm"
            value={novoNome}
            placeholder="Ex.: Instalação"
            onChange={(e) => setNovoNome(e.target.value)}
          />
        </div>
        <div className="w-36">
          <Label htmlFor="nova-origem-modo">Modo</Label>
          <Select
            id="nova-origem-modo"
            size="sm"
            value={novoModo}
            onChange={(e) => setNovoModo(e.target.value as OrigemModo)}
          >
            <SelectOption value="custo_venda">custo × fator</SelectOption>
            <SelectOption value="venda_custo">venda ÷ fator</SelectOption>
          </Select>
        </div>
        <div className="w-20">
          <Label htmlFor="nova-origem-fator">Fator</Label>
          <Input
            id="nova-origem-fator"
            size="sm"
            value={novoFator}
            onChange={(e) => setNovoFator(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') adicionar();
            }}
          />
        </div>
        <Button size="sm" onClick={adicionar} disabled={!novoNome.trim() || createOrigem.isPending}>
          Adicionar
        </Button>
      </div>
    </Card>
  );
}

// ─── 3b. Política de aprovação do Financeiro (FX-22) ────────────────────
// Fase 2 de Compras (decisão do Matheus, 15/09/2026): a POLÍTICA DE APROVAÇÃO DO FINANCEIRO MORREU —
// era uma das cinco razões do abandono ("a aprovação travava"), estava desligada desde julho e ninguém sentiu
// falta. `PoliticaAprovacaoSection` saiu daqui; o store, as rotas, o repo e a tabela saem nos passos seguintes.

// ─── 3c. Crédito por fornecedor (FX-23) ─────────────────────────────────
// ── CreditoFornecedorSection REMOVIDO (mig 467, passo c) ──────────────────────────────
function NotificacoesSection() {
  const { data, isLoading, isError } = useComprasNotifConfig();
  const setConfig = useSetComprasNotifConfig();
  const toast = useToast();
  const membros = data?.items ?? [];

  function salvar(userId: string, patch: { recebeNotif?: boolean; recebeEmailDiario?: boolean }) {
    setConfig.mutate(
      { userId, patch },
      { onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível salvar.', { variant: 'error' }) }
    );
  }

  return (
    <Card variant="flat" className="p-4">
      <h3 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1">
        Notificações do Compras
      </h3>
      <p className="mb-3 mt-0.5 text-sm text-ink-2 dark:text-ink-dark-2">
        Quem da equipe de Compras recebe os alertas (nova solicitação, recebido/entregue, etc.) e o
        e-mail diário com os itens que chegam hoje.
      </p>

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : isError ? (
        <Alert variant="danger">Não foi possível carregar a equipe de Compras.</Alert>
      ) : membros.length === 0 ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">
          Nenhum membro na equipe de Compras ainda.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {membros.map((m) => (
            <li
              key={m.userId}
              data-testid={`compra-notif-${m.userId}`}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border border-line bg-paper-surface px-3 py-2 dark:border-line-dark dark:bg-paper-dark-surface"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink-1 dark:text-ink-dark-1">{m.nome}</p>
                <p className="truncate text-xs text-ink-3 dark:text-ink-dark-3">{m.email}</p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2">
                <Switch
                  size="sm"
                  label="Notificações"
                  checked={m.recebeNotif}
                  onChange={(e) => salvar(m.userId, { recebeNotif: e.target.checked })}
                />
                <Switch
                  size="sm"
                  label="E-mail diário"
                  checked={m.recebeEmailDiario}
                  onChange={(e) => salvar(m.userId, { recebeEmailDiario: e.target.checked })}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
